import { accounts, currentAccount, ALL_ACCOUNTS } from "./api";

/**
 * The account-aware bits of the UI, kept out of main.ts because they are the parts
 * whose behaviour has to be pinned by tests — everything else in that file is layout.
 */

export type AccountStatus = { id: string; label: string; ok: boolean; error?: string };

/** A row as the aggregate API returns it: tagged with the account it came from. */
export type TaggedRow = {
  key?: string;
  account?: string;
  accountLabel?: string;
  thresholdUsd?: number;
};

function el(tag: string, attrs: Record<string, string> = {}, ...kids: (Node | string)[]): HTMLElement {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  n.append(...kids);
  return n;
}

/** The human label for an account id, falling back to the id — a confirmation that
 *  names no account at all is worse than one naming a number. */
export const labelOf = (id: string): string =>
  accounts().find((a) => a.id === id)?.label ?? id;

/**
 * Sits above the tiles, where it cannot be scrolled past, and is deliberately not
 * dismissible: every figure on the page is understated while it is showing.
 *
 * This is the most important behaviour in the aggregate view. A dashboard that
 * quietly renders a smaller number when one account is unreachable is precisely the
 * failure that let a $11,355 day pass unnoticed.
 */
export function accountWarning(status: AccountStatus[] | undefined): HTMLElement | null {
  const bad = (status ?? []).filter((a) => !a.ok);
  if (!bad.length) return null;

  const names = bad.map((b) => b.label).join(", ");
  const why = bad.map((b) => `${b.label}: ${b.error ?? "unknown error"}`).join("; ");
  return el("div", {
    class: "badge badge-danger",
    role: "alert",
    style: "display:block;margin-bottom:12px;padding:8px 12px;line-height:1.6667",
  },
    `These figures are incomplete — ${names} could not be read (${why}). `
    + `Spend in ${bad.length > 1 ? "those accounts is" : "that account is"} not included below.`);
}

/** Only in the aggregate view: with one account selected every row is that account's,
 *  and a badge on all of them is noise. */
export function accountBadge(row: TaggedRow): HTMLElement | null {
  if (currentAccount() !== ALL_ACCOUNTS) return null;
  return el("span", { class: "badge" }, row.accountLabel ?? labelOf(row.account ?? ""));
}

/** The cap this row is actually judged against. Accounts may differ, so a single
 *  response-level default would mark people over a limit nobody enforces on them. */
export const rowThreshold = (row: TaggedRow, fallback: number): number =>
  row.thresholdUsd ?? fallback;

/**
 * The "default cap" caption, which has no single answer in the aggregate when the
 * accounts cap differently — the merge returns null there rather than presenting one
 * account's cap as everyone's.
 *
 * This is also the guard that stops a missing value taking the page down: it used to
 * be `money(def)` inline, and an aggregate response without the field made that
 * `undefined.toLocaleString()`, which killed the whole view the moment "All" was
 * clicked. Any caption over a number that can be absent belongs behind a helper
 * like this one.
 */
export const capCaption = (def: number | null | undefined): string =>
  typeof def === "number"
    ? `Default cap $${def.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per principal per UTC day`
    : "Cap varies by account";

/**
 * Every confirmation names its account, in every view — not only the aggregate.
 * Operator actions stay enabled from the aggregate because during an incident you
 * kill from wherever you spotted the spend; the safeguard is that the account is
 * read before a reason is typed, and a habit that only holds in one view is no habit.
 */
export const confirmTitle = (row: TaggedRow, verb: string): string =>
  `${verb} ${row.key} in ${row.accountLabel ?? labelOf(row.account ?? currentAccount())}`;

/** The account a row's action must target — the ROW's, never the picker's, which in
 *  the aggregate view is "all" and is refused by the write API. */
export const rowAccount = (row: TaggedRow): string => row.account ?? currentAccount();
